import { injectable, inject } from 'inversify';
import { DDD_SYMBOLS } from '../../infrastructure/di/DDD_SYMBOLS';
import { CreateProjectCommand } from '../../bounded-contexts/strategic-design/application/commands/CreateProjectCommand';
import { CreateProjectHandler } from '../../bounded-contexts/strategic-design/application/handlers/CreateProjectHandler';
import { GetProjectHandler } from '../../bounded-contexts/strategic-design/application/handlers/GetProjectHandler';
import { ListProjectsHandler } from '../../bounded-contexts/strategic-design/application/handlers/ListProjectsHandler';
import { GetProjectQuery, ListProjectsQuery } from '../../bounded-contexts/strategic-design/application/queries/GetProjectQuery';
import { ProjectViewModel, ProjectSummaryViewModel, ProjectDashboardViewModel } from '../../bounded-contexts/strategic-design/interface/view-models/ProjectViewModel';
import { CreateProjectRequest, UpdateProjectRequest, GetProjectsQuery } from '../../bounded-contexts/strategic-design/interface/dto/ProjectRequestDTOs';

/**
 * Project Application Service
 * 
 * Orchestrates project operations and provides the boundary between
 * the API layer and the domain/application layers.
 * 
 * This is the ONLY allowed binding point for project API controllers.
 */
@injectable()
export class ProjectApplicationService {
    constructor(
        @inject(DDD_SYMBOLS.CreateProjectHandler) 
        private readonly createProjectHandler: CreateProjectHandler,
        
        @inject(DDD_SYMBOLS.GetProjectHandler)
        private readonly getProjectHandler: GetProjectHandler,
        
        @inject(DDD_SYMBOLS.ListProjectsHandler)
        private readonly listProjectsHandler: ListProjectsHandler
    ) {}

    /**
     * Create a new project
     */
    async createProject(request: CreateProjectRequest): Promise<ProjectViewModel> {
        const command = new CreateProjectCommand(
            {
                name: request.name,
                description: request.description,
                domainId: 'default-domain'
            },
            'api-session',
            'api-user'
        );

        const result = await this.createProjectHandler.handle(command);
        
        return this.mapToProjectViewModel(result);
    }

    /**
     * Get all projects with optional filtering
     */
    async getProjects(query: GetProjectsQuery): Promise<ProjectSummaryViewModel[]> {
        const listQuery = new ListProjectsQuery(
            {
                page: Math.floor(query.offset / query.limit) + 1,
                pageSize: query.limit,
                searchTerm: query.search
            },
            'api-session',
            'api-user'
        );

        const result = await this.listProjectsHandler.handle(listQuery);
        
        return result.items.map((project: any) => ({
            id: project.id,
            name: project.name,
            status: this.mapProjectStatus(project.status),
            team: {
                lead: project.team?.lead,
                memberCount: this.calculateTeamSize(project.team)
            },
            progress: {
                overallCompletion: this.calculateOverallProgress(project),
                domainsCount: project.progress?.domains?.total || 0,
                contextCount: project.progress?.boundedContexts?.total || 0,
                aggregateCount: project.progress?.aggregates?.total || 0
            },
            health: this.calculateProjectHealth(project),
            lastActivity: project.updatedAt || project.createdAt
        }));
    }

    /**
     * Get a specific project by ID
     */
    async getProject(projectId: string): Promise<ProjectViewModel> {
        const query = new GetProjectQuery(projectId, 'api-session', 'api-user');
        const result = await this.getProjectHandler.handle(query);
        
        return this.mapToProjectViewModel(result);
    }

    /**
     * Get project dashboard with metrics and insights
     */
    async getProjectDashboard(projectId: string): Promise<ProjectDashboardViewModel> {
        const project = await this.getProject(projectId);
        
        // These would normally come from analytics/metrics systems
        const metrics = {
            velocity: {
                domainsPerWeek: 0.5,
                aggregatesPerWeek: 2.0,
                trend: 'stable' as const
            },
            quality: {
                averageComplexity: 2.3,
                invariantCoverage: 85,
                documentationScore: 78
            },
            collaboration: {
                activeContributors: project.team.domainExperts.length + project.team.developers.length,
                reviewCycle: 3,
                knowledgeSharing: 82
            }
        };

        const recentActivity = [
            {
                type: 'domain_created' as const,
                description: 'Created new domain: Customer Management',
                timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
                actor: project.team.lead || 'Team Member'
            },
            {
                type: 'aggregate_designed' as const,
                description: 'Designed Order aggregate with 3 entities',
                timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
                actor: 'Domain Expert'
            }
        ];

        const upcomingMilestones = project.timeline.milestones
            .filter(m => new Date(m.targetDate) > new Date())
            .map(milestone => ({
                name: milestone.name,
                targetDate: milestone.targetDate,
                daysRemaining: Math.ceil((new Date(milestone.targetDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
                readiness: this.calculateMilestoneReadiness(milestone, project)
            }))
            .slice(0, 3);

        return {
            project,
            metrics,
            recentActivity,
            upcomingMilestones
        };
    }

    /**
     * Map internal project result to View Model
     */
    private mapToProjectViewModel(result: any): ProjectViewModel {
        return {
            id: result.id || result.projectId,
            name: result.name,
            description: result.description,
            vision: result.vision,
            status: this.mapProjectStatus(result.status),
            createdAt: result.createdAt || new Date().toISOString(),
            updatedAt: result.updatedAt || result.createdAt || new Date().toISOString(),
            team: {
                lead: result.team?.lead,
                domainExperts: result.team?.domainExperts || [],
                developers: result.team?.developers || [],
                stakeholders: result.team?.stakeholders || []
            },
            scope: {
                businessArea: result.scope?.businessArea || '',
                objectives: result.scope?.objectives || [],
                constraints: result.scope?.constraints || [],
                assumptions: result.scope?.assumptions || []
            },
            progress: {
                domains: {
                    total: result.progress?.domains?.total || 0,
                    completed: result.progress?.domains?.completed || 0,
                    inProgress: result.progress?.domains?.inProgress || 0
                },
                boundedContexts: {
                    total: result.progress?.boundedContexts?.total || 0,
                    completed: result.progress?.boundedContexts?.completed || 0,
                    inProgress: result.progress?.boundedContexts?.inProgress || 0
                },
                aggregates: {
                    total: result.progress?.aggregates?.total || 0,
                    designed: result.progress?.aggregates?.designed || 0,
                    implemented: result.progress?.aggregates?.implemented || 0
                }
            },
            timeline: {
                startDate: result.timeline?.startDate || new Date().toISOString(),
                targetDate: result.timeline?.targetDate,
                milestones: (result.timeline?.milestones || []).map((milestone: any) => ({
                    name: milestone.name,
                    targetDate: milestone.targetDate,
                    status: this.mapMilestoneStatus(milestone.status)
                }))
            },
            risks: (result.risks || []).map((risk: any) => ({
                risk: risk.risk,
                impact: risk.impact,
                probability: risk.probability,
                mitigation: risk.mitigation
            }))
        };
    }

    /**
     * Map project status from internal to external representation
     */
    private mapProjectStatus(internalStatus: string | undefined): 'planning' | 'discovery' | 'modeling' | 'implementation' | 'maintenance' | 'archived' {
        switch (internalStatus) {
            case 'PLANNING': return 'planning';
            case 'DISCOVERY': return 'discovery';
            case 'MODELING': return 'modeling';
            case 'IMPLEMENTATION': return 'implementation';
            case 'MAINTENANCE': return 'maintenance';
            case 'ARCHIVED': return 'archived';
            default: return 'planning';
        }
    }

    /**
     * Map milestone status
     */
    private mapMilestoneStatus(internalStatus: string | undefined): 'planned' | 'in_progress' | 'completed' | 'delayed' {
        switch (internalStatus) {
            case 'PLANNED': return 'planned';
            case 'IN_PROGRESS': return 'in_progress';
            case 'COMPLETED': return 'completed';
            case 'DELAYED': return 'delayed';
            default: return 'planned';
        }
    }

    /**
     * Calculate team size
     */
    private calculateTeamSize(team: any): number {
        if (!team) return 0;
        return (team.domainExperts?.length || 0) + 
               (team.developers?.length || 0) + 
               (team.stakeholders?.length || 0) +
               (team.lead ? 1 : 0);
    }

    /**
     * Calculate overall project progress
     */
    private calculateOverallProgress(project: any): number {
        const domains = project.progress?.domains;
        const contexts = project.progress?.boundedContexts;
        const aggregates = project.progress?.aggregates;
        
        if (!domains && !contexts && !aggregates) return 0;
        
        const domainProgress = domains ? (domains.completed / Math.max(domains.total, 1)) * 100 : 0;
        const contextProgress = contexts ? (contexts.completed / Math.max(contexts.total, 1)) * 100 : 0;
        const aggregateProgress = aggregates ? (aggregates.implemented / Math.max(aggregates.total, 1)) * 100 : 0;
        
        return Math.round((domainProgress + contextProgress + aggregateProgress) / 3);
    }

    /**
     * Calculate project health
     */
    private calculateProjectHealth(project: any): 'excellent' | 'good' | 'concerning' | 'critical' {
        const overallProgress = this.calculateOverallProgress(project);
        const teamSize = this.calculateTeamSize(project.team);
        const hasRecentActivity = project.updatedAt && 
            (Date.now() - new Date(project.updatedAt).getTime()) < (7 * 24 * 60 * 60 * 1000);
        
        let healthScore = 0;
        
        if (overallProgress > 80) healthScore += 3;
        else if (overallProgress > 60) healthScore += 2;
        else if (overallProgress > 30) healthScore += 1;
        
        if (teamSize >= 5) healthScore += 2;
        else if (teamSize >= 3) healthScore += 1;
        
        if (hasRecentActivity) healthScore += 1;
        
        if (healthScore >= 5) return 'excellent';
        if (healthScore >= 3) return 'good';
        if (healthScore >= 1) return 'concerning';
        return 'critical';
    }

    /**
     * Calculate milestone readiness
     */
    private calculateMilestoneReadiness(milestone: any, project: any): number {
        // Simplified calculation - would be more sophisticated in real implementation
        const overallProgress = this.calculateOverallProgress(project);
        const daysUntilMilestone = Math.ceil((new Date(milestone.targetDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        
        if (daysUntilMilestone <= 0) return overallProgress;
        if (daysUntilMilestone > 30) return Math.min(overallProgress + 20, 100);
        
        return Math.min(overallProgress + (30 - daysUntilMilestone), 100);
    }
}